// ./src/components/features/VolunteerSlots/VolunteerSlotsFilterBar/VolunteerSlotsFilterBar.tsx
//
// Filter controls for the event Volunteers list: by status (active / disabled) and by
// role. Fully controlled — the page owns the value and maps it to a VolunteerSlotFilter.
// Role options come from the page (it already loads the account's roles for headings).

import React from "react";
import { Group, SegmentedControl, Input, Button, Box } from "@mantine/core";
import { useTranslation } from "react-i18next";

import { AppIcon, CheckableMultiSelect } from "@/components/generic";

export type VolunteerSlotStatusFilter = "active" | "disabled";

export interface VolunteerSlotsFilterValue {
  status: VolunteerSlotStatusFilter | null;
  roleIds: string[];
}

export interface VolunteerSlotRoleOption {
  value: string;
  label: string;
}

export interface VolunteerSlotsFilterBarProps {
  value: VolunteerSlotsFilterValue;
  onChange: (value: VolunteerSlotsFilterValue) => void;
  roleOptions: VolunteerSlotRoleOption[];
  disabled?: boolean;
  showClear?: boolean;
}

export const EMPTY_VOLUNTEER_SLOTS_FILTER: VolunteerSlotsFilterValue = {
  status: null,
  roleIds: [],
};

const isFilterActive = (value: VolunteerSlotsFilterValue): boolean => Boolean(value.status) || value.roleIds.length > 0;

const FACET_STYLE: React.CSSProperties = { flex: "1 1 240px", minWidth: 220 };

const ANY = "any";

const VolunteerSlotsFilterBarComponent: React.FC<VolunteerSlotsFilterBarProps> = ({
  value,
  onChange,
  roleOptions,
  disabled = false,
  showClear = true,
}) => {
  const { t } = useTranslation(["volunteers", "common"]);

  const statusOptions = [
    { value: ANY, label: t("volunteers:filters.status.any", { defaultValue: "Any" }) },
    { value: "active", label: t("volunteers:filters.status.active", { defaultValue: "Active" }) },
    { value: "disabled", label: t("volunteers:filters.status.disabled", { defaultValue: "Disabled" }) },
  ];

  const active = isFilterActive(value);

  return (
    <Group gap="sm" align="flex-end" wrap="wrap">
      <Box style={FACET_STYLE}>
        <Input.Wrapper label={t("volunteers:filters.status.label", { defaultValue: "Status" })}>
          <SegmentedControl
            data={statusOptions}
            value={value.status ?? ANY}
            onChange={(next) => onChange({ ...value, status: next === ANY ? null : (next as VolunteerSlotStatusFilter) })}
            disabled={disabled}
            fullWidth
            size="md"
            mt={4}
          />
        </Input.Wrapper>
      </Box>

      <Box style={FACET_STYLE}>
        <CheckableMultiSelect
          label={t("volunteers:filters.role.label", { defaultValue: "Role" })}
          placeholder={t("volunteers:filters.role.placeholder", { defaultValue: "All roles" })}
          data={roleOptions}
          value={value.roleIds}
          onChange={(ids) => onChange({ ...value, roleIds: ids })}
          disabled={disabled || roleOptions.length === 0}
          size="md"
        />
      </Box>

      {active && showClear && (
        <Button
          variant="subtle"
          color="gray"
          size="md"
          onClick={() => onChange(EMPTY_VOLUNTEER_SLOTS_FILTER)}
          disabled={disabled}
          leftSection={<AppIcon iconName="X" size={16} />}
        >
          {t("common:buttons.clear", { defaultValue: "Clear" })}
        </Button>
      )}
    </Group>
  );
};

export const VolunteerSlotsFilterBar = React.memo(VolunteerSlotsFilterBarComponent);
